import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, RefreshCw, Pencil, KeyRound, Copy, Check, Shield, Eye } from 'lucide-react';
import { authAPI, adminUsersAPI, type AdminUserRow } from '../services/api';
import { getVersionString } from '../version';

const DEFAULT_PASSWORD = 'Sas2026Admin@';

type Role = 'sasadmin' | 'sys-operator';

export default function AdminUserManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentEmail, setCurrentEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminUserRow | null>(null);
  const [resetTarget, setResetTarget] = useState<AdminUserRow | null>(null);

  useEffect(() => { void load(); }, []);

  async function load() {
    setRefreshing(true);
    setError(null);
    try {
      const auth = await authAPI.checkAdminAuth();
      if (!auth.authenticated) { navigate('/admin'); return; }
      if (auth.user?.role !== 'sasadmin') { navigate('/admin/dashboard'); return; }
      setCurrentEmail(auth.user.email ?? null);
      const res = await adminUsersAPI.list();
      setUsers(res.users);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  return (
    <div className="min-h-screen bg-warm-gray-50 dark:bg-warm-gray-900">
      <main className="container mx-auto px-4 py-6 max-w-6xl">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-warm-gray-900 dark:text-white flex items-center gap-2">
              <Shield className="w-6 h-6 text-pink-500" />
              {t('adminUsers.title')}
              <span className="text-xs font-normal text-warm-gray-500 dark:text-warm-gray-400">{getVersionString()}</span>
            </h1>
            <p className="text-sm text-warm-gray-500 dark:text-warm-gray-400 mt-1">{t('adminUsers.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => void load()}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold disabled:opacity-50"
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {t('common.refresh')}
            </button>
            <button
              onClick={() => setCreateOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold"
            >
              <Plus className="w-4 h-4" />
              {t('adminUsers.create')}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-warm-gray-400">{t('common.loading')}</div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-warm-gray-400">{t('adminUsers.noUsers')}</div>
        ) : (
          <div className="overflow-x-auto bg-white dark:bg-warm-gray-800 rounded-xl border border-warm-gray-200 dark:border-warm-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-warm-gray-50 dark:bg-warm-gray-900/50 text-warm-gray-600 dark:text-warm-gray-400">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">{t('adminUsers.email')}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t('adminUsers.name')}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t('adminUsers.role')}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t('adminUsers.status')}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t('adminUsers.createdAt')}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t('adminUsers.lastLogin')}</th>
                  <th className="text-right px-4 py-3 font-semibold">{t('adminUsers.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const isSelf = currentEmail && u.email.toLowerCase() === currentEmail.toLowerCase();
                  return (
                    <tr key={u.id} className="border-t border-warm-gray-200 dark:border-warm-gray-700">
                      <td className="px-4 py-2 font-mono text-xs">
                        {u.email}
                        {isSelf && <span className="ml-2 text-amber-600 dark:text-amber-400 font-sans">{t('adminUsers.you')}</span>}
                      </td>
                      <td className="px-4 py-2">{u.name}</td>
                      <td className="px-4 py-2">
                        {u.role === 'sasadmin' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                            <Shield className="w-3 h-3" /> {t('adminUsers.roleSasadmin')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                            <Eye className="w-3 h-3" /> {t('adminUsers.roleSysOperator')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                          u.enabled
                            ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                            : 'bg-warm-gray-100 dark:bg-warm-gray-700 text-warm-gray-600 dark:text-warm-gray-400'
                        }`}>
                          {u.enabled ? t('adminUsers.enabled') : t('adminUsers.disabled')}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-warm-gray-500">{fmt(u.createdAt)}</td>
                      <td className="px-4 py-2 text-xs text-warm-gray-500">{fmt(u.lastLogin)}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {isSelf ? (
                          <span className="text-xs text-warm-gray-400">—</span>
                        ) : (
                          <div className="inline-flex gap-1">
                            <button
                              onClick={() => setEditTarget(u)}
                              className="px-2 py-1 rounded text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200"
                              title={t('adminUsers.edit')}
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => setResetTarget(u)}
                              className="px-2 py-1 rounded text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 hover:bg-orange-200"
                              title={t('adminUsers.resetPassword')}
                            >
                              <KeyRound className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {createOpen && <CreateModal onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); void load(); }} />}
      {editTarget && <EditModal target={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); void load(); }} />}
      {resetTarget && <ResetPasswordModal target={resetTarget} onClose={() => setResetTarget(null)} onDone={() => { setResetTarget(null); void load(); }} />}
    </div>
  );
}

function fmt(iso: string | null): string {
  if (!iso) return '—';
  try {
    // JST presentation (customer rule 2026-06-26): always Asia/Tokyo, 24-hour.
    return new Date(iso).toLocaleString('ja-JP', {
      timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch {
    return iso;
  }
}

function CreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('sys-operator');
  const [password, setPassword] = useState(DEFAULT_PASSWORD);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      await adminUsersAPI.create({ email, name, role, password });
      onCreated();
    } catch (e: any) {
      setError(e?.message ?? 'Failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title={t('adminUsers.createTitle')} onClose={onClose}>
      {error && <FieldError msg={error} />}
      <Field label={t('adminUsers.email')}>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputCls} />
      </Field>
      <Field label={t('adminUsers.name')}>
        <input type="text" value={name} onChange={e => setName(e.target.value)} className={inputCls} />
      </Field>
      <Field label={t('adminUsers.role')}>
        <select value={role} onChange={e => setRole(e.target.value as Role)} className={inputCls}>
          <option value="sys-operator">{t('adminUsers.roleSysOperator')}</option>
          <option value="sasadmin">{t('adminUsers.roleSasadmin')}</option>
        </select>
      </Field>
      <Field label={t('adminUsers.defaultPasswordLabel')} hint={t('adminUsers.passwordHint')}>
        <input type="text" value={password} onChange={e => setPassword(e.target.value)} className={inputCls} />
      </Field>
      <ModalActions>
        <button onClick={onClose} className={cancelBtnCls}>{t('common.cancel')}</button>
        <button onClick={() => void submit()} disabled={submitting || !email || !name} className={primaryBtnCls}>
          {submitting ? t('common.generating') : t('common.confirm')}
        </button>
      </ModalActions>
    </ModalShell>
  );
}

function EditModal({ target, onClose, onSaved }: { target: AdminUserRow; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState(target.name);
  const [role, setRole] = useState<Role>(target.role);
  const [enabled, setEnabled] = useState(target.enabled);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      await adminUsersAPI.update(target.id, { name, role, enabled });
      onSaved();
    } catch (e: any) {
      setError(e?.message ?? 'Failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title={t('adminUsers.editTitle')} onClose={onClose}>
      {error && <FieldError msg={error} />}
      <div className="text-xs text-warm-gray-500 mb-2 font-mono">{target.email}</div>
      <Field label={t('adminUsers.name')}>
        <input type="text" value={name} onChange={e => setName(e.target.value)} className={inputCls} />
      </Field>
      <Field label={t('adminUsers.role')}>
        <select value={role} onChange={e => setRole(e.target.value as Role)} className={inputCls}>
          <option value="sys-operator">{t('adminUsers.roleSysOperator')}</option>
          <option value="sasadmin">{t('adminUsers.roleSasadmin')}</option>
        </select>
      </Field>
      <Field label={t('adminUsers.status')}>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          <span>{enabled ? t('adminUsers.enabled') : t('adminUsers.disabled')}</span>
        </label>
      </Field>
      <ModalActions>
        <button onClick={onClose} className={cancelBtnCls}>{t('common.cancel')}</button>
        <button onClick={() => void submit()} disabled={submitting} className={primaryBtnCls}>
          {submitting ? t('common.generating') : t('common.save')}
        </button>
      </ModalActions>
    </ModalShell>
  );
}

function ResetPasswordModal({ target, onClose, onDone }: { target: AdminUserRow; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState(DEFAULT_PASSWORD);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      await adminUsersAPI.resetPassword(target.id, password);
      onDone();
    } catch (e: any) {
      setError(e?.message ?? 'Failed');
    } finally {
      setSubmitting(false);
    }
  }

  function copyPassword() {
    navigator.clipboard.writeText(password).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <ModalShell title={t('adminUsers.resetTitle')} onClose={onClose}>
      {error && <FieldError msg={error} />}
      <p className="text-sm text-warm-gray-700 dark:text-warm-gray-300 mb-3">
        {t('adminUsers.confirmReset', { email: target.email })}
      </p>
      <Field label={t('adminUsers.defaultPasswordLabel')} hint={t('adminUsers.passwordHint')}>
        <div className="flex gap-2">
          <input type="text" value={password} onChange={e => setPassword(e.target.value)} className={inputCls} />
          <button onClick={copyPassword} className="px-3 py-2 rounded bg-warm-gray-200 dark:bg-warm-gray-700 text-sm whitespace-nowrap inline-flex items-center gap-1">
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? t('adminUsers.copied') : t('adminUsers.copyDefault')}
          </button>
        </div>
      </Field>
      <ModalActions>
        <button onClick={onClose} className={cancelBtnCls}>{t('common.cancel')}</button>
        <button onClick={() => void submit()} disabled={submitting} className={primaryBtnCls}>
          {submitting ? t('common.generating') : t('adminUsers.resetPassword')}
        </button>
      </ModalActions>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-warm-gray-900 dark:text-white mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ children }: { children: React.ReactNode }) {
  return <div className="flex justify-end gap-2 mt-5">{children}</div>;
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-semibold text-warm-gray-600 dark:text-warm-gray-400 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-warm-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

function FieldError({ msg }: { msg: string }) {
  return <div className="mb-3 px-3 py-2 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-xs">{msg}</div>;
}

const inputCls = 'w-full px-3 py-2 rounded border border-warm-gray-300 dark:border-warm-gray-600 bg-white dark:bg-warm-gray-900 text-warm-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
const primaryBtnCls = 'px-4 py-2 rounded bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold disabled:opacity-50';
const cancelBtnCls = 'px-4 py-2 rounded border border-warm-gray-300 dark:border-warm-gray-600 text-warm-gray-700 dark:text-warm-gray-300 text-sm';

// satisfy unused-import warning when role badges aren't rendered
void useMemo;
